import React, { useMemo, useState } from "react";

import Avatar from "@material-ui/core/Avatar";
import AvatarGroup from "@material-ui/lab/AvatarGroup";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Divider from "@material-ui/core/Divider";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";

import { descreverContatos, telefoneParaConversar } from "../../helpers/vcard";
import useConversarContato from "./useConversarContato";

/// a partir daqui o modal ganha busca — rolar procurando um nome deixa de ser prático ///
const MINIMO_PARA_BUSCA = 6;

const useStyles = makeStyles((theme) => ({
  card: {
    minWidth: 250,
    maxWidth: 320,
  },
  cabecalho: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "6px 4px 10px",
    minWidth: 0,
  },
  avatares: {
    flexShrink: 0,
    "& .MuiAvatar-root": {
      width: 36,
      height: 36,
      fontSize: 13,
      /// o balão muda de cor conforme o lado e o tema: borda neutra serve nos dois ///
      border: "2px solid rgba(0, 0, 0, 0.25)",
    },
  },
  resumo: {
    minWidth: 0,
    fontWeight: 600,
    lineHeight: 1.25,
    wordBreak: "break-word",
  },
  titulo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    paddingRight: theme.spacing(1),
  },
  busca: {
    padding: theme.spacing(0, 3, 1.5),
  },
  conteudo: {
    padding: 0,
  },
  linha: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: theme.spacing(1.25, 3),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1.25, 2),
    },
  },
  info: {
    flex: 1,
    minWidth: 0,
  },
  nome: {
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  detalhe: {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  botao: {
    flexShrink: 0,
    minWidth: 96,
  },
  vazio: {
    padding: theme.spacing(4, 3),
    textAlign: "center",
  },
}));

const normalizar = (texto) =>
  String(texto || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const casaComBusca = (contato, busca) => {
  if (!busca) return true;
  const termo = normalizar(busca);
  const digitos = busca.replace(/\D/g, "");
  if (normalizar(contato.nome).includes(termo)) return true;
  if (contato.empresa && normalizar(contato.empresa).includes(termo)) return true;
  return !!digitos && contato.telefones.some((t) => t.digitos.includes(digitos));
};

const LinhaContato = ({ contato, conversar, podeConversar, carregando, aoAbrir }) => {
  const classes = useStyles();
  const telefone = telefoneParaConversar(contato);
  const abrindo = !!telefone && carregando === telefone.digitos;

  return (
    <div className={classes.linha}>
      <Avatar src={contato.foto || undefined} />
      <div className={classes.info}>
        <Typography variant="body1" color="primary" className={classes.nome} title={contato.nome}>
          {contato.nome || "Contato sem nome"}
        </Typography>
        {contato.empresa && (
          <Typography variant="caption" color="textSecondary" className={classes.detalhe}>
            {contato.empresa}
          </Typography>
        )}
        {contato.telefones.map((tel, indice) => (
          <Typography key={`${tel.digitos}-${indice}`} variant="body2" className={classes.detalhe}>
            {tel.numero}
            {tel.rotulo && (
              <Typography component="span" variant="caption" color="textSecondary">
                {` · ${tel.rotulo}`}
              </Typography>
            )}
          </Typography>
        ))}
      </div>
      <Button
        size="small"
        color="primary"
        variant="outlined"
        className={classes.botao}
        onClick={() => conversar(contato, aoAbrir)}
        disabled={!podeConversar(contato)}
        title={telefone ? undefined : "Este contato não tem telefone"}
      >
        {abrindo ? <CircularProgress size={18} /> : "Conversar"}
      </Button>
    </div>
  );
};

// Vários contatos numa mensagem só: um card de resumo, como o WhatsApp, e a lista inteira
// num modal — empilhar um card por contato no balão não escala.
const VcardGrupoPreview = ({ contatos, whatsappId }) => {
  const classes = useStyles();
  const theme = useTheme();
  const telaPequena = useMediaQuery(theme.breakpoints.down("xs"));
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState("");
  const { conversar, podeConversar, carregando } = useConversarContato(whatsappId);

  const filtrados = useMemo(
    () =>
      contatos
        .map((contato, indice) => ({ contato, indice }))
        .filter(({ contato }) => casaComBusca(contato, busca)),
    [contatos, busca]
  );

  const fechar = () => {
    setAberto(false);
    setBusca("");
  };

  return (
    <div className={classes.card}>
      <div className={classes.cabecalho}>
        <AvatarGroup max={3} className={classes.avatares}>
          {contatos.map((contato, indice) => (
            <Avatar key={indice} src={contato.foto || undefined} alt={contato.nome} />
          ))}
        </AvatarGroup>
        <Typography variant="subtitle1" color="primary" className={classes.resumo}>
          {descreverContatos(contatos)}
        </Typography>
      </div>
      <Divider />
      <Button fullWidth color="primary" onClick={() => setAberto(true)}>
        Ver todos
      </Button>

      <Dialog
        open={aberto}
        onClose={fechar}
        fullScreen={telaPequena}
        fullWidth
        maxWidth="xs"
        scroll="paper"
      >
        <DialogTitle disableTypography className={classes.titulo}>
          <Typography variant="h6">{contatos.length} contatos</Typography>
          <IconButton aria-label="Fechar" onClick={fechar}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        {contatos.length >= MINIMO_PARA_BUSCA && (
          <div className={classes.busca}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Buscar por nome ou número"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </div>
        )}
        <DialogContent dividers className={classes.conteudo}>
          {filtrados.length === 0 ? (
            <Typography color="textSecondary" className={classes.vazio}>
              Nenhum contato encontrado
            </Typography>
          ) : (
            filtrados.map(({ contato, indice }, posicao) => (
              <React.Fragment key={indice}>
                {posicao > 0 && <Divider component="div" />}
                <LinhaContato
                  contato={contato}
                  conversar={conversar}
                  podeConversar={podeConversar}
                  carregando={carregando}
                  aoAbrir={fechar}
                />
              </React.Fragment>
            ))
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VcardGrupoPreview;
